import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription, from } from 'rxjs';
import { ContactForm } from 'src/app/classes/form-groups/contact-form';
import {
  ClientRegisteredVendor,
  Contact,
  ExemptVendor,
  Vendor
} from 'src/app/classes/models';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { EditContactTypeModalComponent } from 'src/app/modals/edit-contact-type-modal/edit-contact-type-modal.component';
import { ClientRegisteredVendorService } from 'src/app/services/client-registered-vendor.service';
import { ContactService } from 'src/app/services/contact.service';
import { ExemptVendorService } from 'src/app/services/exempt-vendor.service';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';
import { VendorService } from 'src/app/services/vendor.service';

@Component({
  selector: 'app-admin-vendor-contacts',
  templateUrl: './admin-vendor-contacts.component.html',
  styleUrls: ['./admin-vendor-contacts.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AdminVendorContactsComponent {
  constructor(
    private VendorService: VendorService,
    private ExemptVendorService: ExemptVendorService,
    private ClientRegisteredVendorService: ClientRegisteredVendorService,
    private SharedDataService: SharedDataService,
    private ToastService: ToastService,
    private ModalService: NgbModal,
    private ContactService: ContactService,
    private UtilityService: UtilityService,
    private ChangeDetectorRef: ChangeDetectorRef
  ) {
    const vendorQuery = {
      attributes: ['id', 'legalName'],
      Contact: {}
    };

    this.SharedDataServiceSubscription =
      this.SharedDataService.listenForEvent().subscribe({
        next: ({ eventName, data }) => {
          if (eventName === 'SetVendorId' && typeof data === 'number') {
            this.VendorId = data;

            this.VendorService.getVendorV2(
              this.VendorId,
              this.UtilityService.stringifyObjectsInQueryObject(vendorQuery)
            ).subscribe({
              next: (Vendor) => {
                this.setContacts(Vendor);
              }
            });
          }

          if (eventName === 'SetExemptVendorId' && typeof data === 'number') {
            this.ExemptVendorId = data;
            Object.assign(vendorQuery, { id: data });

            this.ExemptVendorService.getExemptVendor(
              this.UtilityService.stringifyObjectsInQueryObject(vendorQuery)
            ).subscribe({
              next: (ExemptVendor) => {
                this.setContacts(ExemptVendor);
              }
            });
          }

          if (
            eventName === 'SetClientRegisteredVendorId' &&
            typeof data === 'number'
          ) {
            this.ClientRegisteredVendorId = data;
            Object.assign(vendorQuery, { id: data });

            this.ClientRegisteredVendorService.getClientRegisteredVendor(
              this.UtilityService.stringifyObjectsInQueryObject(vendorQuery)
            ).subscribe({
              next: (ClientRegisteredVendor) => {
                this.setContacts(ClientRegisteredVendor);
              }
            });
          }
        }
      });

    this.SharedDataService.sendEvent('GetVendorId');
  }

  PrimaryContactFlags: (
    'isPrimaryBid' | 'isPrimaryBilling' | 'isPrimaryCompliance'
  )[] = ['isPrimaryBid', 'isPrimaryBilling', 'isPrimaryCompliance'];

  SharedDataServiceSubscription?: Subscription;

  VendorId?: number;
  ExemptVendorId?: number;
  ClientRegisteredVendorId?: number;
  Contacts: Contact[] = [];

  PrimaryTypeToContact: {
    isPrimaryBid?: Contact;
    isPrimaryBilling?: Contact;
    isPrimaryCompliance?: Contact;
  } = {};

  setContacts(Vendor: Vendor | ExemptVendor | ClientRegisteredVendor) {
    const { legalName, Contacts } = Vendor;

    if (!legalName) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Vendor legal name.',
        { returnError: true }
      );
    }

    if (Contacts) {
      Contacts.forEach((Contact) => {
        this.PrimaryContactFlags.forEach((contactType) => {
          if (Contact[contactType]) {
            Object.assign(this.PrimaryTypeToContact, {
              [contactType]: Contact
            });
          }
        });

        this.Contacts.push(Contact);
      });
    }

    // Sort the contacts with primary coming first.
    this.Contacts.sort((a, b) => {
      const isFirstContactPrimary = !!this.PrimaryContactFlags.find(
        (flag) => a[flag]
      );
      const isSecondContactPrimary = !!this.PrimaryContactFlags.find(
        (flag) => b[flag]
      );
      if (isFirstContactPrimary && isSecondContactPrimary) {
        return 0;
      } else if (isFirstContactPrimary) {
        return -1;
      } else if (isSecondContactPrimary) {
        return 1;
      }

      if (!a.isBid && !a.isBilling && !a.isCompliance) {
        return 1;
      } else {
        return -1;
      }
    });

    this.SharedDataService.sendEvent('SetTitle', `Contacts - ${legalName}`);

    this.ChangeDetectorRef.markForCheck();
  }

  getContactTitle(Contact: Contact) {
    const {
      isBid,
      isBilling,
      isCompliance,
      isPrimaryBid,
      isPrimaryBilling,
      isPrimaryCompliance
    } = Contact;

    const TitleParts: string[] = [];

    if (isPrimaryBid) {
      TitleParts.push('Primary Bid');
    } else if (isBid) {
      TitleParts.push('Bid');
    }

    if (isPrimaryBilling) {
      TitleParts.push('Primary Billing');
    } else if (isBilling) {
      TitleParts.push('Billing');
    }

    if (isPrimaryCompliance) {
      TitleParts.push('Primary Compliance');
    } else if (isCompliance) {
      TitleParts.push('Compliance');
    }

    if (!isBid && !isBilling && !isCompliance) {
      TitleParts.push('Additional');
    }

    return TitleParts;

    // May opt for this. Let team decide.
    // return [TitleParts.join(', '), 'Contact'].join(' ');
  }

  changeContact(Contact: Contact) {
    if (
      !this.VendorId &&
      !this.ExemptVendorId &&
      !this.ClientRegisteredVendorId
    ) {
      throw this.ToastService.addErrorToast('Error', 'Missing Vendor Id.', {
        returnError: true
      });
    }

    const { componentInstance, result } = this.ModalService.open(
      EditContactTypeModalComponent,
      { backdrop: 'static', size: 'lg' }
    );

    const ContactFormGroup = new ContactForm();
    ContactFormGroup.patchValue(Contact);

    if (this.VendorId) {
      Object.assign(componentInstance, {
        VendorId: this.VendorId,
        ContactFormGroup
      });
    }

    if (this.ExemptVendorId) {
      Object.assign(componentInstance, {
        ExemptVendorId: this.ExemptVendorId,
        ContactFormGroup
      });
    }

    if (this.ClientRegisteredVendorId) {
      Object.assign(componentInstance, {
        ClientRegisteredVendorId: this.ClientRegisteredVendorId,
        ContactFormGroup
      });
    }

    from(result).subscribe({
      next: (SavedContact: Contact) => {
        const MergedContact = Object.assign(Contact, SavedContact);
        this.PrimaryContactFlags.forEach((contactType) => {
          const FoundPrimaryContact = this.PrimaryTypeToContact[contactType];
          if (MergedContact[contactType]) {
            if (
              FoundPrimaryContact &&
              FoundPrimaryContact.id !== MergedContact.id
            ) {
              Object.assign(FoundPrimaryContact, { [contactType]: false });
            }
            Object.assign(this.PrimaryTypeToContact, {
              [contactType]: MergedContact
            });
          }
        });
        this.ChangeDetectorRef.markForCheck();
      }
    });
  }

  deleteContact(Contact: Contact, index: number) {
    const { id: ContactId } = Contact;

    if (!ContactId) {
      throw this.ToastService.addErrorToast('Error', 'Contact is missing id.', {
        returnError: true
      });
    }

    const { componentInstance, result } = this.ModalService.open(
      ConfirmModalComponent,
      { backdrop: 'static', size: 'md' }
    );

    Object.assign(componentInstance, {
      displayText:
        'Deleting this contact will also delete the associated user. Do you want to continue?'
    });

    from(result).subscribe({
      next: (response) => {
        if (response) {
          this.ContactService.deleteContact(ContactId).subscribe({
            next: () => {
              this.Contacts.splice(index, 1);
              this.ToastService.addSuccessToast(
                'Success',
                'Successfully deleted contact.'
              );
              this.ChangeDetectorRef.markForCheck();
            }
          });
        }
      }
    });
  }

  ngOnDestroy() {
    if (this.SharedDataServiceSubscription) {
      this.SharedDataServiceSubscription.unsubscribe();
    }
  }
}
